import type { Prisma } from '@prisma/client';
import { registerImportEntity, type ImportEntity } from '../csv/csv.registry.js';
import { issuesToErrors, parseBool } from '../parties/parties.import.js';
import {
  itemInclude,
  itemInputSchema,
  itemReferenceErrors,
  saveItem,
  type ItemInput,
} from './catalog.service.js';

const columns = [
  {
    key: 'name',
    label: 'Name',
    required: true,
    example: 'Website maintenance (monthly)',
    aliases: ['item', 'product', 'service'],
  },
  { key: 'sku', label: 'SKU', example: 'SVC-MAINT-01', aliases: ['code'] },
  { key: 'type', label: 'Type (service/product/domain/hosting/subscription)', example: 'service' },
  { key: 'description', label: 'Description', example: 'Monthly patching and backups' },
  { key: 'unit', label: 'Unit', example: 'month' },
  { key: 'vat_code', label: 'VAT category', example: 'VAT_STD' },
  { key: 'revenue_account_key', label: 'Revenue account', example: 'REV_CONSULT' },
  { key: 'cost_price', label: 'Cost price', example: '' },
  { key: 'price_ugx', label: 'Price (UGX)', example: '250000', aliases: ['price'] },
  { key: 'price_usd', label: 'Price (USD)', example: '' },
  { key: 'active', label: 'Active', example: 'yes' },
];

const labels = Object.fromEntries(columns.map((c) => [c.key, c.label]));

const itemEntity: ImportEntity<ItemInput> = {
  key: 'items',
  label: 'Products & Services',
  importPermission: 'sales.item.import',
  columns,
  parseRow(raw) {
    const errors: string[] = [];
    const prices = [
      ...(raw.price_ugx?.trim() ? [{ currency: 'UGX', price: raw.price_ugx.trim() }] : []),
      ...(raw.price_usd?.trim() ? [{ currency: 'USD', price: raw.price_usd.trim() }] : []),
    ];
    const parsed = itemInputSchema.safeParse({
      name: raw.name,
      sku: raw.sku ?? '',
      type: raw.type?.trim().toLowerCase() || 'service',
      description: raw.description ?? '',
      unit: raw.unit?.trim() || 'each',
      vatCode: raw.vat_code?.trim().toUpperCase() || 'VAT_STD',
      revenueAccountKey: raw.revenue_account_key?.trim().toUpperCase() || undefined,
      costPrice: raw.cost_price ?? '',
      active: parseBool(raw.active || 'yes', 'Active', errors),
      prices,
    });
    if (!parsed.success)
      errors.push(
        ...issuesToErrors(parsed.error, {
          ...labels,
          revenueAccountKey: 'Revenue account',
          vatCode: 'VAT category',
          costPrice: 'Cost price',
          prices: 'Price',
        }),
      );
    if (errors.length || !parsed.success) return { ok: false, errors };
    return { ok: true, value: parsed.data };
  },
  rowKey: (v) => (v.sku ? `sku:${v.sku.toLowerCase()}` : null),
  async validate(ctx, v) {
    return itemReferenceErrors(ctx.tx, ctx.companyId, v);
  },
  async findExisting(ctx, v) {
    if (!v.sku) return null;
    const item = await ctx.tx.item.findUnique({
      where: { companyId_sku: { companyId: ctx.companyId, sku: v.sku } },
      include: itemInclude,
    });
    return item ? { id: item.id, snapshot: JSON.parse(JSON.stringify(item)) } : null;
  },
  async create(ctx, v) {
    const item = await saveItem(ctx.tx, { companyId: ctx.companyId, userId: ctx.userId, input: v });
    return { id: item.id };
  },
  async update(ctx, id, v) {
    await saveItem(ctx.tx, { companyId: ctx.companyId, userId: ctx.userId, id, input: v });
  },
  async restore(ctx, id, before) {
    const {
      prices,
      id: _i,
      companyId: _c,
      createdAt: _ca,
      updatedAt: _ua,
      customFields,
      costPrice,
      ...cols
    } = before as Record<string, unknown> & { prices: { currency: string; price: string }[] };
    await ctx.tx.item.update({
      where: { id },
      data: {
        ...(cols as Prisma.ItemUpdateInput),
        costPrice: costPrice as string | null,
        customFields: (customFields ?? undefined) as Prisma.InputJsonValue | undefined,
      },
    });
    await ctx.tx.itemPrice.deleteMany({ where: { itemId: id } });
    if (prices.length)
      await ctx.tx.itemPrice.createMany({
        data: prices.map((p) => ({ itemId: id, currency: p.currency, price: p.price })),
      });
  },
  async remove(ctx, id) {
    try {
      await ctx.tx.item.delete({ where: { id } });
      return null;
    } catch {
      return 'this item is now used on documents and cannot be removed';
    }
  },
};

registerImportEntity(itemEntity);
